import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

// Enhanced Social Media Controls Component with X.com and Nostr Integration
const EnhancedXComControls: React.FC = () => {
  // X.com states
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');
  const [tweetText, setTweetText] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);
  
  // Nostr states
  const [nostrConnectionStatus, setNostrConnectionStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');
  const [nostrText, setNostrText] = useState('');
  const [isPostingNostr, setIsPostingNostr] = useState(false);
  const [nostrResult, setNostrResult] = useState<any>(null);
  const [nostrStatus, setNostrStatus] = useState<any>(null);
  
  // Rate limit status
  const [rateLimitInfo, setRateLimitInfo] = useState<any>(null);
  const [isCheckingRateLimit, setIsCheckingRateLimit] = useState(false);
  
  // Image upload states (shared between both platforms)
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [croppedPreviews, setCroppedPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Scheduling states
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');
  const [scheduledTweets, setScheduledTweets] = useState<any[]>([]);
  const [schedulingStats, setSchedulingStats] = useState<any>(null);

  // Test connections on component mount
  useEffect(() => {
    testConnection();
    testNostrConnection();
    loadScheduledTweets();
    loadSchedulingStats();
    checkRateLimit();
  }, []);

  const testConnection = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/x/test`);
      if (response.data.success) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('error');
      }
    } catch (error) {
      setConnectionStatus('error');
    }
  };

  const testNostrConnection = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/nostr/status`);
      if (response.data.configured) {
        setNostrConnectionStatus('connected');
        setNostrStatus(response.data);
      } else {
        setNostrConnectionStatus('error');
        setNostrStatus(response.data);
      }
    } catch (error) {
      setNostrConnectionStatus('error');
      console.error('Nostr connection test failed:', error);
    }
  };

  const checkRateLimit = async () => {
    try {
      setIsCheckingRateLimit(true);
      const response = await axios.get(`${apiBase}/api/x/rate-limit-status`);
      if (response.data.success !== false) {
        setRateLimitInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to check rate limit:', error);
    } finally {
      setIsCheckingRateLimit(false);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    // X.com supports up to 4 images
    const limitedFiles = files.slice(0, 4);
    const newImages = [...selectedImages, ...limitedFiles].slice(0, 4);
    
    setSelectedImages(newImages);
    
    // Create previews for all images
    const newPreviews: string[] = [];
    const newCroppedPreviews: string[] = [];
    
    newImages.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imgSrc = e.target?.result as string;
        newPreviews[index] = imgSrc;
        
        // Create 16:9 cropped preview
        create16x9Preview(imgSrc, index, newCroppedPreviews, newImages.length);
        
        if (newPreviews.length === newImages.length) {
          setImagePreviews([...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const create16x9Preview = (imageSrc: string, index: number, croppedArray: string[], totalImages: number) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set 16:9 dimensions
      const targetWidth = 400;
      const targetHeight = 225; // 400/225 = 16:9
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      
      // Calculate crop area for 16:9
      const targetRatio = targetWidth / targetHeight;
      const imageRatio = img.width / img.height;
      
      let sourceX = 0, sourceY = 0, sourceWidth = img.width, sourceHeight = img.height;
      
      if (imageRatio > targetRatio) {
        // Image is wider - crop sides
        sourceWidth = img.height * targetRatio;
        sourceX = (img.width - sourceWidth) / 2;
      } else {
        // Image is taller - crop top/bottom
        sourceHeight = img.width / targetRatio;
        sourceY = (img.height - sourceHeight) / 2;
      }
      
      // Draw cropped image
      ctx?.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, targetWidth, targetHeight
      );
      
      croppedArray[index] = canvas.toDataURL();
      
      // Update state when all images are processed
      if (croppedArray.filter(Boolean).length === totalImages) {
        setCroppedPreviews([...croppedArray]);
      }
    };
    img.src = imageSrc;
  };

  const clearImages = () => {
    setSelectedImages([]);
    setImagePreviews([]);
    setCroppedPreviews([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    const newCroppedPreviews = croppedPreviews.filter((_, i) => i !== index);
    
    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
    setCroppedPreviews(newCroppedPreviews);
  };

  const postTweet = async () => {
    if (!tweetText.trim()) return;
    
    setIsPosting(true);
    try {
      let response;
      
      if (selectedImages.length > 0) {
        // Post with multiple images
        const formData = new FormData();
        formData.append('text', tweetText);
        
        selectedImages.forEach((image, index) => {
          formData.append('images', image);
        });
        
        response = await axios.post(`${apiBase}/api/x/tweet-with-images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Post text only
        response = await axios.post(`${apiBase}/api/x/tweet`, {
          text: tweetText
        });
      }
      
      setLastResult(response.data);
      if (response.data.success) {
        setTweetText('');
        clearImages();
      }
      
      // Refresh rate limit info after posting
      await checkRateLimit();
    } catch (error: any) {
      const errorData = error.response?.data;
      setLastResult({ 
        success: false, 
        error: errorData?.error || 'Failed to post tweet',
        detailedError: errorData?.detailedError // Display detailed rate limit info
      });
      
      // Refresh rate limit info even on error
      await checkRateLimit();
    } finally {
      setIsPosting(false);
    }
  };

  const scheduleTweet = async () => {
    if (!tweetText.trim() || !scheduledTime) return;
    
    setIsPosting(true);
    try {
      const formData = new FormData();
      formData.append('text', tweetText);
      formData.append('scheduledTime', scheduledTime);
      
      selectedImages.forEach((image, index) => {
        formData.append('images', image);
      });
      
      const response = await axios.post(`${apiBase}/api/x/schedule-tweet`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setLastResult(response.data);
      if (response.data.success) {
        setTweetText('');
        setScheduledTime('');
        clearImages();
        loadScheduledTweets();
        loadSchedulingStats();
      }
    } catch (error: any) {
      setLastResult({ 
        success: false, 
        error: error.response?.data?.error || 'Failed to schedule tweet' 
      });
    } finally {
      setIsPosting(false);
    }
  };

  const loadScheduledTweets = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/x/scheduled-tweets`);
      if (response.data.success) {
        setScheduledTweets(response.data.tweets);
      }
    } catch (error) {
      console.error('Failed to load scheduled tweets:', error);
    }
  };

  const loadSchedulingStats = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/x/scheduling-stats`);
      if (response.data.success) {
        setSchedulingStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to load scheduling stats:', error);
    }
  };

  const cancelScheduledTweet = async (scheduleId: number) => {
    try {
      const response = await axios.delete(`${apiBase}/api/x/scheduled-tweets/${scheduleId}`);
      if (response.data.success) {
        loadScheduledTweets();
        loadSchedulingStats();
      }
    } catch (error) {
      console.error('Failed to cancel scheduled tweet:', error);
    }
  };

  const postLeaderboard = async () => {
    setIsPosting(true);
    try {
      const response = await axios.post(`${apiBase}/api/x/leaderboard`);
      setLastResult(response.data);
    } catch (error: any) {
      setLastResult({ 
        success: false, 
        error: error.response?.data?.error || 'Failed to post leaderboard' 
      });
    } finally {
      setIsPosting(false);
    }
  };

  // Nostr functions
  const postToNostr = async () => {
    if (!nostrText.trim()) return;
    
    setIsPostingNostr(true);
    try {
      let response;
      
      if (selectedImages.length > 0) {
        // Post with images
        const formData = new FormData();
        formData.append('content', nostrText);
        
        selectedImages.forEach((image, index) => {
          formData.append('images', image);
        });
        
        response = await axios.post(`${apiBase}/api/nostr/post`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Post text only
        response = await axios.post(`${apiBase}/api/nostr/post`, {
          content: nostrText
        });
      }
      
      setNostrResult(response.data);
      if (response.data.success) {
        setNostrText('');
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      setNostrResult({ 
        success: false, 
        error: errorData?.error || 'Failed to post to Nostr',
        details: errorData?.details
      });
    } finally {
      setIsPostingNostr(false);
    }
  };

  // Dual posting function
  const postToBothPlatforms = async () => {
    if (!tweetText.trim() && !nostrText.trim()) return;
    
    setIsPosting(true);
    setIsPostingNostr(true);
    
    const results: any = {};
    
    try {
      // Post to X.com if text is provided
      if (tweetText.trim()) {
        try {
          let xResponse;
          
          if (selectedImages.length > 0) {
            const formData = new FormData();
            formData.append('text', tweetText);
            selectedImages.forEach((image) => {
              formData.append('images', image);
            });
            
            xResponse = await axios.post(`${apiBase}/api/x/tweet-with-images`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } else {
            xResponse = await axios.post(`${apiBase}/api/x/tweet`, { text: tweetText });
          }
          
          results.x = xResponse.data;
        } catch (error: any) {
          results.x = { 
            success: false, 
            error: error.response?.data?.error || 'Failed to post to X.com',
            detailedError: error.response?.data?.detailedError
          };
        }
      }
      
      // Post to Nostr if text is provided
      if (nostrText.trim()) {
        try {
          let nostrResponse;
          
          if (selectedImages.length > 0) {
            const formData = new FormData();
            formData.append('content', nostrText);
            selectedImages.forEach((image) => {
              formData.append('images', image);
            });
            
            nostrResponse = await axios.post(`${apiBase}/api/nostr/post`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } else {
            nostrResponse = await axios.post(`${apiBase}/api/nostr/post`, { content: nostrText });
          }
          
          results.nostr = nostrResponse.data;
        } catch (error: any) {
          results.nostr = { 
            success: false, 
            error: error.response?.data?.error || 'Failed to post to Nostr'
          };
        }
      }
      
      // Set results for both platforms
      setLastResult(results.x);
      setNostrResult(results.nostr);
      
      // Clear text fields if both posts were successful
      if (results.x?.success && results.nostr?.success) {
        setTweetText('');
        setNostrText('');
        clearImages();
      }
      
      // Refresh rate limit info for X.com
      if (results.x) {
        await checkRateLimit();
      }
      
    } finally {
      setIsPosting(false);
      setIsPostingNostr(false);
    }
  };

  // Get minimum datetime for scheduling (5 minutes from now)
  const getMinScheduleTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    return now.toISOString().slice(0, 16);
  };

  return (
    <SocialMediaSection>
      <MainTitle>🌐 Social Media Dashboard - X.com & Nostr</MainTitle>
      
      {/* Shared Image Upload Section */}
      <SharedImageSection>
        <h3>📸 Image Upload (Shared)</h3>
        <ImageUploadControls>
          <UploadButton onClick={() => fileInputRef.current?.click()}>
            📤 Add Images (max 4)
          </UploadButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
          {selectedImages.length > 0 && (
            <ClearButton onClick={clearImages}>
              �️ Clear All ({selectedImages.length})
            </ClearButton>
          )}
        </ImageUploadControls>

        {selectedImages.length > 0 && (
          <ImagePreviewSection>
            <PreviewColumn>
              <h5>Original Images</h5>
              <ImageGrid>
                {imagePreviews.map((preview, index) => (
                  <ImagePreviewContainer key={index}>
                    <PreviewImage src={preview} alt={`Original ${index + 1}`} />
                    <RemoveImageButton onClick={() => removeImage(index)}>
                      ❌
                    </RemoveImageButton>
                  </ImagePreviewContainer>
                ))}
              </ImageGrid>
            </PreviewColumn>
            <PreviewColumn>
              <h5>16:9 Cropped Previews</h5>
              <ImageGrid>
                {croppedPreviews.map((preview, index) => (
                  <PreviewImage key={index} src={preview} alt={`Cropped ${index + 1}`} />
                ))}
              </ImageGrid>
              <PreviewNote>These cropped versions will be posted to both platforms</PreviewNote>
            </PreviewColumn>
          </ImagePreviewSection>
        )}
      </SharedImageSection>

      {/* Dual Platform Interface */}
      <DualPlatformContainer>
        {/* X.com (Twitter) Section */}
        <PlatformSection>
          <SectionTitle>🐦 X.com (Twitter)</SectionTitle>
          
          <ConnectionStatus status={connectionStatus}>
            <strong>Status: </strong>
            {connectionStatus === 'connected' && '✅ Connected'}
            {connectionStatus === 'error' && '❌ Connection Error'}
            {connectionStatus === 'unknown' && '⏳ Testing...'}
            <button onClick={testConnection} style={{ marginLeft: '10px', fontSize: '12px' }}>
              Test
            </button>
          </ConnectionStatus>

          {/* Rate Limit Status */}
          {rateLimitInfo && (
            <RateLimitStatus canPost={rateLimitInfo.canPost}>
              <strong>Rate Limit: </strong>
              {rateLimitInfo.canPost ? (
                <span>✅ {rateLimitInfo.remaining}/{rateLimitInfo.limit} remaining</span>
              ) : (
                <span>⏰ {rateLimitInfo.minutesUntilReset}min wait</span>
              )}
            </RateLimitStatus>
          )}

          <TweetTextArea
            value={tweetText}
            onChange={(e) => setTweetText(e.target.value)}
            placeholder="Write your X.com post here..."
            maxLength={4000}
          />
          <CharCount>
            {tweetText.length}/4000 characters
          </CharCount>

          <PostButton 
            onClick={postTweet}
            disabled={isPosting || !tweetText.trim() || connectionStatus !== 'connected'}
          >
            {isPosting ? '⏳ Posting...' : '🐦 Post to X.com'}
          </PostButton>

          {/* X.com Result */}
          {lastResult && (
            <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', 
                         backgroundColor: lastResult.success ? '#d4edda' : '#f8d7da' }}>
              {lastResult.success ? (
                <div>
                  <strong>✅ Posted successfully!</strong>
                  {lastResult.tweet?.id && (
                    <div style={{ marginTop: '5px', fontSize: '0.9em' }}>
                      Tweet ID: {lastResult.tweet.id}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <strong>❌ Error: {lastResult.error}</strong>
                  {lastResult.detailedError && (
                    <div style={{ marginTop: '5px', fontSize: '0.9em', whiteSpace: 'pre-wrap' }}>
                      {lastResult.detailedError}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </PlatformSection>

        {/* Nostr Section */}
        <PlatformSection>
          <SectionTitle>⚡ Nostr</SectionTitle>
          
          <ConnectionStatus status={nostrConnectionStatus}>
            <strong>Status: </strong>
            {nostrConnectionStatus === 'connected' && '✅ Connected'}
            {nostrConnectionStatus === 'error' && '❌ Connection Error'}
            {nostrConnectionStatus === 'unknown' && '⏳ Testing...'}
            <button onClick={testNostrConnection} style={{ marginLeft: '10px', fontSize: '12px' }}>
              Test
            </button>
          </ConnectionStatus>

          {/* Nostr Status Info */}
          {nostrStatus && (
            <div style={{ margin: '10px 0', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '0.9em' }}>
              {nostrStatus.configured ? (
                <div>
                  <div><strong>Public Key:</strong> {nostrStatus.publicKey?.substring(0, 20)}...</div>
                  <div><strong>Relays:</strong> {nostrStatus.connectedRelays}/{nostrStatus.totalRelays} connected</div>
                </div>
              ) : (
                <div style={{ color: '#856404' }}>
                  <strong>⚠️ {nostrStatus.error}</strong>
                  <div style={{ marginTop: '5px', fontSize: '0.8em' }}>
                    Add your NOSTR_PRIVATE_KEY to the .env file to enable posting
                  </div>
                </div>
              )}
            </div>
          )}

          <TweetTextArea
            value={nostrText}
            onChange={(e) => setNostrText(e.target.value)}
            placeholder="Write your Nostr note here..."
          />
          <CharCount>
            {nostrText.length} characters
          </CharCount>

          <PostButton 
            onClick={postToNostr}
            disabled={isPostingNostr || !nostrText.trim() || nostrConnectionStatus !== 'connected'}
          >
            {isPostingNostr ? '⏳ Posting...' : '⚡ Post to Nostr'}
          </PostButton>

          {/* Nostr Result */}
          {nostrResult && (
            <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', 
                         backgroundColor: nostrResult.success ? '#d4edda' : '#f8d7da' }}>
              {nostrResult.success ? (
                <div>
                  <strong>✅ Posted successfully!</strong>
                  <div style={{ marginTop: '5px', fontSize: '0.9em' }}>
                    Published to {nostrResult.publishedToRelays}/{nostrResult.totalRelays} relays
                  </div>
                  {nostrResult.eventId && (
                    <div style={{ fontSize: '0.8em', wordBreak: 'break-all' }}>
                      Event ID: {nostrResult.eventId}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <strong>❌ Error: {nostrResult.error}</strong>
                  {nostrResult.details && (
                    <div style={{ marginTop: '5px', fontSize: '0.9em' }}>
                      {nostrResult.details}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </PlatformSection>
      </DualPlatformContainer>

      {/* Dual Post Button */}
      <DualPostButton 
        onClick={postToBothPlatforms}
        disabled={isPosting || isPostingNostr || (!tweetText.trim() && !nostrText.trim())}
      >
        {(isPosting || isPostingNostr) ? '⏳ Posting to Both Platforms...' : '🚀 Post to Both Platforms'}
      </DualPostButton>

    </SocialMediaSection>
  );
};

// Styled components
const SocialMediaSection = styled.div`
  background: ${props => props.theme.surface || '#ffffff'};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const MainTitle = styled.h2`
  color: ${props => props.theme.text || '#333333'};
  margin-bottom: 20px;
  font-size: 24px;
  text-align: center;
`;

const SharedImageSection = styled.div`
  border: 2px solid ${props => props.theme.primary || '#1da1f2'};
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  background: ${props => props.theme.surface || '#f8f9fa'};
`;

const ImageUploadControls = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
`;

const UploadButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #45a049;
  }
`;

const ClearButton = styled.button`
  background: #f44336;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #da190b;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.border || '#ddd'};
`;

const ImagePreviewSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 15px;
`;

const PreviewColumn = styled.div`
  h5 {
    color: ${props => props.theme.text || '#333333'};
    margin-bottom: 10px;
    font-size: 14px;
  }
`;

const PreviewNote = styled.div`
  color: ${props => props.theme.subText || '#666666'};
  font-size: 12px;
  margin-top: 8px;
  font-style: italic;
`;

const DualPlatformContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlatformSection = styled.div`
  border: 2px solid ${props => props.theme.border || '#ddd'};
  border-radius: 8px;
  padding: 15px;
  background: ${props => props.theme.surface || '#f8f9fa'};
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.text || '#333333'};
  margin-bottom: 15px;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConnectionStatus = styled.div<{ status: string }>`
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  background: ${props => {
    if (props.status === 'connected') return '#4CAF5020';
    if (props.status === 'error') return '#f4433620';
    return '#ff980020';
  }};
  border: 1px solid ${props => {
    if (props.status === 'connected') return '#4CAF50';
    if (props.status === 'error') return '#f44336';
    return '#ff9800';
  }};
  color: ${props => props.theme.text || '#333333'};
  font-size: 14px;
`;

const RateLimitStatus = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'canPost'
})<{ canPost: boolean }>`
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  background: ${props => props.canPost ? '#4CAF5020' : '#ff980020'};
  border: 1px solid ${props => props.canPost ? '#4CAF50' : '#ff9800'};
  color: ${props => props.theme.text || '#333333'};
  font-size: 14px;
`;

const TweetTextArea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 15px;
  border: 1px solid ${props => props.theme.border || '#ddd'};
  border-radius: 8px;
  background: ${props => props.theme.background || '#ffffff'};
  color: ${props => props.theme.text || '#333333'};
  resize: vertical;
  font-family: inherit;
  font-size: 16px;
  line-height: 1.5;
  min-height: 120px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary || '#1da1f2'};
    box-shadow: 0 0 0 2px ${props => props.theme.primary || '#1da1f2'}20;
  }
`;

const CharCount = styled.div`
  text-align: right;
  color: ${props => props.theme.subText || '#666666'};
  font-size: 12px;
  margin-top: 5px;
  margin-bottom: 15px;
`;

const PostButton = styled.button`
  background: #1DA1F2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  width: 100%;
  margin-bottom: 10px;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background: #0d8bd9;
  }
`;

const NostrPostButton = styled.button`
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  width: 100%;
  margin-bottom: 10px;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background: #7c3aed;
  }
`;

const DualPostButton = styled.button`
  background: linear-gradient(45deg, #1da1f2, #8b5cf6);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  margin: 10px 0;
  font-size: 16px;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export default EnhancedXComControls;
